// Pure helpers to compare two time off plan versions (`version.data`): flatten each plan into
// readable rows ("Accrual › Eligible pay codes › REG") and diff them. No React / i18n in here:
// labels come back as segments ({ i18n } when the form has a translation key, { text } otherwise).

// Audit / technical fields: never shown.
export const IGNORED_KEYS = new Set([
    "_id",
    "id",
    "createdAt",
    "updatedAt",
    "createdBy",
    "updatedBy",
    "deletedAt",
    "deletedBy",
    "company",
    "mainCompany",
    "entityId",
    "startEffectiveDate",
    "endEffectiveDate",
    "pendingUpdate",
    "pendingUpdateAppliedAt",
    "version",
    "__v",
]);

// Translation keys the form already uses, by key path (array indexes dropped).
export const LABEL_KEYS = {
    code: "name",
    description: "description",
    status: "status",
    payCode: "pay-code",
    canAccrue: "can-accrue",
    baseOnExpectedHours: "base-on-expected-hours",
    tierBy: "tier-by",
    tiers: "tiers",
    "tiers.name": "name",
    "tiers.start": "start",
    "tiers.end": "end",
    accural: "accrual",
    "accural.method": "accrual-method",
    "accural.type": "earning-type",
    "accural.payCodes": "eligible-pay-codes",
    "accural.earningGroups": "earning-groups",
    "accural.groupings": "groupings",
    "accural.accrueEveryPeriod": "accrue-every-pay-period",
    "accural.intervals": "intervals",
    "accural.intervals.earned": "earned",
    "accural.intervals.worked": "worked",
    "accural.intervals.max": "maximum",
    "accural.intervals.value": "value",
    "accural.intervals.start": "start",
    "accural.intervals.end": "end",
    "accural.intervals.name": "name",
    minRequestedHours: "min-requested-hours",
    maxRequestedHours: "max-hours-allowed-to-be-taken",
    allowNegativeBalance: "allow-negative-balance",
    minBalance: "min-balance",
    maxBalance: "max-balance",
    maxBalanceStatus: "max-balance",
    carryover: "carryover",
    carryoverStatus: "carryover",
    reset: "reset",
    "reset.type": "type",
    "reset.value": "date",
    prohibition: "probation",
    "prohibition.value": "days",
    "prohibition.allowAccrual": "allow-accrual-during-probation-period",
    "prohibition.allowRequests": "allow-user-to-make-request-during-probation-period",
    "prohibition.payCode": "pay-code",
};

// Fallback translation keys by the last key alone (any nesting).
const LEAF_LABEL_KEYS = {
    name: "name",
    start: "start",
    end: "end",
    value: "value",
    earned: "earned",
    worked: "worked",
    max: "max",
    type: "type",
    status: "status",
    description: "description",
};

// String values that are enum options the form shows translated.
export const ENUM_PATHS = new Set(["status", "tierBy", "accural.method", "accural.type", "reset.type"]);

// Arrays with one item per tier: their items are labelled with the tier name.
export const TIER_ARRAYS = new Set([
    "tiers",
    "accural.intervals",
    "maxBalance",
    "minBalance",
    "carryover",
    "maxRequestedHours",
]);

// Row order: the form's order; other fields after these, in the order they appear.
const FIELD_ORDER = [
    "code",
    "description",
    "status",
    "canAccrue",
    "baseOnExpectedHours",
    "payCode",
    "tierBy",
    "tiers",
    "accural",
    "minBalance",
    "allowNegativeBalance",
    "minRequestedHours",
    "maxRequestedHours",
    "maxBalanceStatus",
    "maxBalance",
    "carryoverStatus",
    "carryover",
    "prohibition",
    "reset",
];

const isPlainObject = (value) =>
    value !== null && typeof value === "object" && !Array.isArray(value);

// A looked-up resource (pay code, earning group, ...): shown by its code.
const isLookup = (value) =>
    isPlainObject(value) && (typeof value.code === "string" || typeof value.code === "number");

/** Map id -> code of every looked-up resource in the given plans, to show raw ids by code too. */
export const buildIdIndex = (...values) => {
    const index = {};
    const walk = (value) => {
        if (Array.isArray(value)) {
            value.forEach(walk);
        } else if (isPlainObject(value)) {
            if (isLookup(value) && typeof value.id === "string") {
                index[value.id] = String(value.code);
            }
            Object.values(value).forEach(walk);
        }
    };
    values.forEach(walk);
    return index;
};

const resolveScalar = (value, idIndex) => {
    if (isLookup(value)) {
        return String(value.code);
    }
    if (typeof value === "string" && idIndex[value] !== undefined) {
        return idIndex[value];
    }
    return value;
};

/** "accrueEveryPeriod" / "allow-requests" / "max_value" -> "Accrue every period" ... */
export const humanizeKey = (key) => {
    const text = String(key)
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[-_.]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    return text.charAt(0).toUpperCase() + text.slice(1);
};

const keySegment = (key, schemaPath) => {
    const i18n = LABEL_KEYS[schemaPath] || (schemaPath.includes(".") ? LEAF_LABEL_KEYS[key] : undefined);
    return i18n ? { i18n } : { text: humanizeKey(key) };
};

const isEmptyValue = (value) =>
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0) ||
    (isPlainObject(value) && Object.keys(value).length === 0);

/**
 * Flatten one plan into rows `{ key, schemaPath, segments, value, enum }`.
 * - plain objects: one row per field (nested);
 * - looked-up objects (with `code`) and raw ids of known lookups: the code;
 * - arrays of scalars / lookups: one row per item, keyed by its code, value `true`;
 * - arrays of objects: one group per item, labelled with the tier name for per-tier arrays.
 */
export const flattenPlan = (data, { idIndex = {} } = {}) => {
    const rows = [];
    if (!isPlainObject(data)) {
        return rows;
    }
    const tierNames = Array.isArray(data.tiers) ? data.tiers.map((tier) => tier?.name) : [];

    const addLeaf = (key, schemaPath, segments, value) => {
        rows.push({
            key,
            schemaPath,
            segments,
            value: isEmptyValue(value) ? null : value,
            enum: ENUM_PATHS.has(schemaPath),
        });
    };

    const walk = (value, key, schemaPath, segments) => {
        // The plan itself has a `code` too: only nested objects are lookups.
        if ((key && isLookup(value)) || !(isPlainObject(value) || Array.isArray(value))) {
            addLeaf(key, schemaPath, segments, resolveScalar(value, idIndex));
            return;
        }

        if (isPlainObject(value)) {
            const entries = Object.entries(value).filter(([k]) => !IGNORED_KEYS.has(k));
            if (entries.length === 0) {
                addLeaf(key, schemaPath, segments, null);
                return;
            }
            entries.forEach(([k, v]) => {
                const childSchema = schemaPath ? `${schemaPath}.${k}` : k;
                walk(v, key ? `${key}.${k}` : k, childSchema, [...segments, keySegment(k, childSchema)]);
            });
            return;
        }

        // Arrays
        if (value.length === 0) {
            addLeaf(key, schemaPath, segments, null);
            return;
        }
        const scalarItems = value.every((item) => isLookup(item) || !(isPlainObject(item) || Array.isArray(item)));
        if (scalarItems) {
            value.forEach((item) => {
                const resolved = resolveScalar(item, idIndex);
                if (isEmptyValue(resolved)) {
                    return;
                }
                const label = String(resolved);
                addLeaf(`${key}[${label}]`, schemaPath, [...segments, { text: label }], true);
            });
            return;
        }
        value.forEach((item, index) => {
            const tierName = TIER_ARRAYS.has(schemaPath) ? tierNames[index] : undefined;
            const itemSegment = { text: tierName ? String(tierName) : `#${index + 1}` };
            walk(item, `${key}[${index}]`, schemaPath, [...segments, itemSegment]);
        });
    };

    walk(data, "", "", []);
    return rows;
};

// Values that mean the same thing compare equal: 160 / "160", null / "" / [].
export const normalizeValue = (value) => {
    if (isEmptyValue(value)) {
        return null;
    }
    if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
        return Number(value);
    }
    return value;
};

const parentKey = (key) => key.replace(/(\.[^.[\]]+|\[[^\]]*\])$/, "");

/**
 * Compare two plans (`version.data`). Returns rows `{ key, segments, a, b, enum, different }`,
 * in the form's field order; a field missing on one side has value `null` (or `undefined`
 * for one item of a list) on that side.
 */
export const compareVersions = (dataA, dataB) => {
    const idIndex = buildIdIndex(dataA, dataB);
    const rowsA = flattenPlan(dataA, { idIndex });
    const rowsB = flattenPlan(dataB, { idIndex });

    const byKey = new Map();
    const appearance = new Map();
    const remember = (row) => {
        if (!appearance.has(row.key)) {
            appearance.set(row.key, appearance.size);
        }
        const parent = parentKey(row.key);
        if (!appearance.has(`parent:${parent}`)) {
            appearance.set(`parent:${parent}`, appearance.size);
        }
    };
    rowsA.forEach((row) => {
        remember(row);
        byKey.set(row.key, { key: row.key, segments: row.segments, enum: row.enum, a: row.value, b: undefined });
    });
    rowsB.forEach((row) => {
        remember(row);
        const existing = byKey.get(row.key);
        if (existing) {
            existing.b = row.value;
            existing.segments = row.segments;
        } else {
            byKey.set(row.key, { key: row.key, segments: row.segments, enum: row.enum, a: undefined, b: row.value });
        }
    });

    const rank = (key) => {
        const top = key.split(/[.[]/)[0];
        const index = FIELD_ORDER.indexOf(top);
        return index === -1 ? FIELD_ORDER.length : index;
    };

    return Array.from(byKey.values())
        .map((row) => ({ ...row, different: normalizeValue(row.a) !== normalizeValue(row.b) }))
        .sort((x, y) =>
            rank(x.key) - rank(y.key) ||
            appearance.get(`parent:${parentKey(x.key)}`) - appearance.get(`parent:${parentKey(y.key)}`) ||
            appearance.get(x.key) - appearance.get(y.key)
        );
};

/**
 * Versions picked by default: current vs pending when a change is scheduled, otherwise the
 * previous (latest finished) vs current. Older first. `versions` newest first, no deleted ones.
 */
export const defaultComparePair = (versions) => {
    const list = versions || [];
    if (list.length < 2) {
        return [list[0]?.id || null, null];
    }
    const current = list.find((version) => version.status === "current");
    const pending = list.find((version) => version.status === "pending");
    if (current && pending) {
        return [current.id, pending.id];
    }
    if (current) {
        const previous = list
            .filter((version) => version.status === "finished")
            .sort((x, y) => String(y.startEffectiveDate).localeCompare(String(x.startEffectiveDate)))[0];
        if (previous) {
            return [previous.id, current.id];
        }
    }
    return [list[1].id, list[0].id];
};
